import maplibregl from 'maplibre-gl';
import { getWsUrl } from '../config';

// Store retry counts per raceId to avoid global counter issues
const retryCountMap = new Map<string, number>();
const MAX_RETRIES = 5;
const addedLayers = new Set<string>();

// Clear tracking when style changes (called externally)
export const clearMVTTracking = (map: maplibregl.Map, raceId: string) => {
  const layerKey = `${raceId}-${map.getContainer().id}`;
  addedLayers.delete(layerKey);
};

export const addMVTLayers = (map: maplibregl.Map, raceId: string, pilotColors?: Map<string, string>, autoLoad: boolean = false) => {
  // Only auto-load MVT if explicitly requested
  if (!autoLoad) {
    // MVT layers not auto-loaded
    return;
  }

  if (!map || !raceId) {
    // Cannot add MVT: missing parameters
    return;
  }

  // Check if we've already successfully added layers for this race
  const layerKey = `${raceId}-${map.getContainer().id}`;
  if (addedLayers.has(layerKey)) {
    // MVT layers already added
    return;
  }

  // Get or initialize retry count for this raceId
  const retryCount = retryCountMap.get(raceId) || 0;

  // Check if style is loaded
  try {
    if (!map.isStyleLoaded()) {
      // Schedule a retry instead of registering event listener
      if (retryCount < MAX_RETRIES) {
        retryCountMap.set(raceId, retryCount + 1);
        setTimeout(() => {
          addMVTLayers(map, raceId);
        }, 200);
      } else {
        // Max retries reached
        retryCountMap.delete(raceId);
      }
      return;
    }
  } catch (e) {
    // If isStyleLoaded throws (which can happen if no style), retry later
    if (retryCount < MAX_RETRIES) {
      retryCountMap.set(raceId, retryCount + 1);
      // Style check failed, retrying
      setTimeout(() => {
        addMVTLayers(map, raceId);
      }, 200);
    } else {
      // Max retries reached
      retryCountMap.delete(raceId);
    }
    return;
  }

  // Reset retry count on successful style check
  retryCountMap.delete(raceId);

  // Remove existing source if it exists
  if (map.getSource('live-tracking')) {
    // Removing existing MVT source
    try {
      if (map.getLayer('flight-paths')) {
        map.removeLayer('flight-paths');
      }
    } catch (e) {
      // flight-paths layer not found
    }
    try {
      if (map.getLayer('track-points-mvt')) {
        map.removeLayer('track-points-mvt');
      }
    } catch (e) {
      // track-points-mvt layer not found
    }
    try {
      map.removeSource('live-tracking');
    } catch (e) {
      // Error removing source
    }
  }

  const baseUrl = getWsUrl('').replace('ws://', 'http://').replace('wss://', 'https://').replace(/\/$/, '');
  const mvtUrl = `${baseUrl}/tracking/public-mvt/${raceId}/{z}/{x}/{y}`;

  try {
    // Add the source
    map.addSource('live-tracking', {
      type: 'vector',
      tiles: [mvtUrl],
      minzoom: 4,
      maxzoom: 22,
      // @ts-ignore - volatile is a valid option
      volatile: false
    });

    // Find the best layer to insert after
    const layers = map.getStyle().layers;
    let insertBeforeId: string | undefined = undefined;
    let lastRoadLayerId: string | null = null;

    for (const layer of layers || []) {
      // Track the last road/transport layer
      if (layer.id.includes('road') ||
        layer.id.includes('highway') ||
        layer.id.includes('bridge') ||
        layer.id.includes('tunnel') ||
        layer.id.includes('path') ||
        layer.id.includes('ferry') ||
        layer.id.includes('street') ||
        layer.id.includes('way')) {
        lastRoadLayerId = layer.id;
      }

      // Find first label/symbol layer after roads
      if (!insertBeforeId &&
        (layer.id.includes('label') ||
          layer.id.includes('name') ||
          layer.id.includes('text') ||
          layer.id.includes('symbol') ||
          layer.id.includes('place') ||
          layer.id.includes('poi'))) {
        insertBeforeId = layer.id;
        // Inserting MVT layers
        break;
      }
    }

    // If we didn't find a label layer but found roads, insert after the last road
    if (!insertBeforeId && lastRoadLayerId) {
      const layerIndex = layers?.findIndex(l => l.id === lastRoadLayerId);
      if (layerIndex !== undefined && layerIndex < (layers?.length || 0) - 1) {
        insertBeforeId = layers![layerIndex + 1].id;
        // Inserting MVT layers after roads
      }
    }

    // Use pilot-specific colors if available, otherwise use default palette
    const defaultColors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
      '#98D8C8', '#FFD93D', '#6BCB77', '#9B59B6',
      '#FF8C94', '#4DD0E1'
    ];

    // Build color expression for MVT layers
    const colorExpression: any = [
      'match',
      ['get', 'color_index'],
      0, defaultColors[0],
      1, defaultColors[1],
      2, defaultColors[2],
      3, defaultColors[3],
      4, defaultColors[4],
      5, defaultColors[5],
      6, defaultColors[6],
      7, defaultColors[7],
      8, defaultColors[8],
      9, defaultColors[9],
      '#999999' // fallback color
    ];

    // Add flight paths layer
    map.addLayer({
      id: 'flight-paths',
      type: 'line',
      source: 'live-tracking',
      'source-layer': 'track_lines',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        'visibility': 'visible'
      },
      paint: {
        'line-color': colorExpression,
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 1.5,
          12, 2.5,
          16, 3.5
        ],
        'line-opacity': 0.7
      }
    }, insertBeforeId);

    // Add track points layer
    map.addLayer({
      id: 'track-points-mvt',
      type: 'circle',
      source: 'live-tracking',
      'source-layer': 'track_points',
      layout: {
        'visibility': 'visible'
      },
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 2,
          12, 4,
          16, 6
        ],
        'circle-color': colorExpression,
        'circle-opacity': 0.6,
        'circle-stroke-width': 0
      }
    }, insertBeforeId);

    // Force a repaint
    map.triggerRepaint();

    // Mark as successfully added
    addedLayers.add(layerKey);
    // MVT layers added
  } catch (error) {
    console.error('Error adding MVT source/layers:', error);
  }
};

export const debugMVTLayers = (map: maplibregl.Map) => {
  if (!map) return;

  // MVT DEBUG - commented out for production
  /*
  const flightPathsLayer = map.getLayer('flight-paths');
  const trackPointsLayer = map.getLayer('track-points-mvt');
  const renderedLines = map.queryRenderedFeatures(undefined, { layers: ['flight-paths'] });
  const renderedPoints = map.queryRenderedFeatures(undefined, { layers: ['track-points-mvt'] });
  const sourceFeatures = map.querySourceFeatures('live-tracking');
  const trackLineFeatures = map.querySourceFeatures('live-tracking', { sourceLayer: 'track_lines' });
  const trackPointFeatures = map.querySourceFeatures('live-tracking', { sourceLayer: 'track_points' });
  const currentZoom = map.getZoom();
  const bounds = map.getBounds();
  */
};